// Copyright TraderEvolution Global LTD. © 2017-2026. All rights reserved.
import { DateTimeUtils } from '@shared/utils/Time/DateTimeUtils';
import { Resources } from '@shared/localizations/Resources';
import { BaseItem, ColumnData } from './BaseItem';
import { ColumnParams } from './ColumnParams';
import { Leve2QuoteType } from '@shared/utils/Enums/Constants';
import { WDSettings } from '../settings/WDGeneralSettings';
import { TableTotalsVisibility } from './TableTotalsVisibility';
import { QTAlignment } from '../elements/QuickTable/QuickTableColumn';
export class Level2ItemQ extends BaseItem {
    constructor() {
        super(...arguments);
        this.Instrument = null;
        this.ProductType = null;
        this.Account = null;
        this.insLotSize = 1;
        this.quoteSize = 1;
        this.mdLotsMode = MDLotsMode.General;
        this.asset = null;
        this.QuoteMessage = null;
    }
    // public id = Level2ItemQ.idCount++;
    // Level2ItemQ.idCount = 0;
    isAllowEdit(columnIndex) {
        return columnIndex === 6;
    }
    ColumnCount() {
        return Level2ItemQ.columnsParams.length;
    }
    SetInstrument(value) {
        if (this.Instrument !== value) {
            this.Instrument = value;
            this.refreshInstrumentData();
        }
    }
    SetQuoteMessage(value) {
        this.QuoteMessage = value;
        if (this.QuoteMessage !== null) {
            this.quoteSize = this.QuoteMessage.Size;
        }
        else {
            this.quoteSize = 1;
        }
    }
    SetAccount(acc) {
        if (this.Account !== acc) {
            this.Account = acc;
            this.refreshInstrumentData();
        }
    }
    SetProductType(newPt) {
        if (this.ProductType !== newPt) {
            this.ProductType = newPt;
            this.refreshInstrumentData();
        }
    }
    refreshInstrumentData() {
        const ins = this.Instrument;
        if (ins != null) {
            this.insLotSize = ins.getLotSize();
            this.asset = ins.DataCache.GetAssetByName(ins.Exp2);
            this.sizePrecision = ins.getLotStepPrecision(this.ProductType, this.Account);
        }
        else {
            this.insLotSize = 1;
        }
    }
    getColumnData(column) {
        const value = this.getColumnValue(column);
        let formattedValue = '';
        switch (column) {
            case 1: {
                const ins = this.Instrument;
                if (this.QuoteMessage.QuoteType == Leve2QuoteType.None) {
                    formattedValue = ins.formatPrice(this.QuoteMessage.Price);
                }
                else if (this.QuoteMessage.QuoteType == Leve2QuoteType.Indicative) {
                    formattedValue = isNaN(ins.Level1.GetIndicativeAuctionPrice())
                        ? '-'
                        : ins.Level1.strIndicativeAuctionPrice() + Resources.getResource('panel.level2.Auction');
                }
                else if (this.QuoteMessage.QuoteType == Leve2QuoteType.WithoutPrice) {
                    formattedValue = '-';
                }
                else {
                    formattedValue = 'invalid quote type';
                } // заглушка такого быть не должно
                // formattedValue = isNaN(value) ? "-" : this.Instrument.formatPrice(value);
                break;
            }
            case 2:
                formattedValue = this.GetSizeString(value, this.mdLotsMode, this.RoundedPrecision, this.RoundedPrecisionValue, this.Instrument);
                break;
            case 3:
                formattedValue = DateTimeUtils.FormatToTime(value);
                break;
            case 4:
                formattedValue = this.Instrument.formatPricePrecision(value); // this.Instrument.formatPricePrecision(this.VWAP);
                break;
            case 5:
                formattedValue = this.Instrument.DataCache.formatVolume(this.Instrument, value, WDSettings.isQuantityInLots, WDSettings.productType, this.Account);
                break;
            case 8:
                if (WDSettings.isQuantityInLots) {
                    formattedValue = this.Instrument.DataCache.formatVolume(this.Instrument, value, WDSettings.isQuantityInLots, WDSettings.productType, this.Account);
                }
                else {
                    formattedValue = this.Instrument.formatPrice(value);
                } // this.Instrument.formatPrice(this.QuoteMessage.DayTradeVolume);
                break;
            case 9:
                formattedValue =
                    this.QuoteMessage.QuoteType !== Leve2QuoteType.None ? '-' : this.asset.formatPrice(value);
                break;
            case 10:
                // - обрезает лишнюю точность
                formattedValue = this.asset.formatPrice(value);
                break;
            case 11:
                formattedValue = DateTimeUtils.formatDate(new Date(value), 'HH:mm:ss'); // .toString(); //"hh\:mm\:ss"
                break;
            default:
                if (value !== null) {
                    formattedValue = value.toString();
                }
                else {
                    formattedValue = '';
                }
                break;
        }
        return new ColumnData(value, formattedValue);
    }
    getColumnValue(column) {
        if (this.Instrument == null) {
            return null;
        }
        if (this.QuoteMessage != null) {
            switch (column) {
                case 0:
                    return this.MMPD;
                case 1:
                    return this.QuoteMessage.Price; // Math.round(this.QuoteMessage.Price, this.Instrument.getPrecision());
                case 2: {
                    let inLots = WDSettings.isQuantityInLots;
                    if (this.mdLotsMode === MDLotsMode.Lots) {
                        inLots = true;
                    }
                    else if (this.mdLotsMode === MDLotsMode.RealSize) {
                        inLots = false;
                    }
                    let val = 0;
                    if (inLots) {
                        val = this.quoteSize / this.insLotSize;
                    }
                    else {
                        val = this.quoteSize;
                    }
                    return val;
                }
                case 3:
                    return this.QuoteMessage.cTime; //  this.sessionSettings.ConvertUTCTimeToSelectedTimeZone(this.QuoteMessage.cTime, this.Instrument.DataCache);
                case 4:
                    return this.VWAP;
                case 5:
                    return WDSettings.isQuantityInLots
                        ? this.aggregatedVolume / this.insLotSize
                        : this.aggregatedVolume;
                case 6:
                    return this.QuoteMessage.Source;
                case 7:
                    return this.count;
                case 8:
                    if (WDSettings.isQuantityInLots) {
                        if ((this.Instrument !== null
                            ? this.QuoteMessage.DayTradeVolume / this.insLotSize
                            : this.QuoteMessage.DayTradeVolume) < 0.01) {
                            return this.QuoteMessage.DayTradeVolume;
                        }
                        else {
                            return this.QuoteMessage.DayTradeVolume / this.insLotSize;
                        }
                    }
                    else {
                        return this.QuoteMessage.DayTradeVolume;
                    }
                case 9:
                    return this.QuoteMessage.Price * this.quoteSize * this.Instrument.GetTickCost();
                case 10:
                    return this.aggregatedVolume * this.VWAP * this.Instrument.GetTickCost();
                case 11:
                    return DateTimeUtils.DateTimeUtcNow().getTime() - this.QuoteMessage.cTime;
                case 12:
                    return this.QuoteMessage.NumberOfOrders;
            }
        }
        return null;
    }
    GetColumnParams(column) {
        const result = Level2ItemQ.columnsParams[column];
        const isHidden = result.DefaultHidden || Resources.isHidden(result.HeaderKey);
        result.Hidden = isHidden;
        if (column === 6 || column === 11 || column === 8) {
            result.Hidden = true;
        }
        return result;
    }
    GetCurrentAccount() {
        return this.Account;
    }
    GetCurrentInstrument() {
        return this.Instrument;
    }
    InstrumentName() {
        return this.Instrument != null ? this.Instrument.GetInteriorID() : '';
    }
    Dispose() {
        this.sessionSettings = null;
    }
    GetSizeString(size, mdLotsMode, RoundedPrecision, RoundedPrecisionValue, Instrument) {
        return size.toFixed(this.sizePrecision);
    }
}
/// <summary>
/// Здесь хранятся настройки колонок (один экземпляр на класс)
/// </summary>
Level2ItemQ.columnsParams = [
    new ColumnParams('panel.Level2.MPID', 70, QTAlignment.Left, TableTotalsVisibility.None, false, false),
    new ColumnParams('panel.Level2.Price', 70, QTAlignment.Right, TableTotalsVisibility.None, true, false),
    new ColumnParams('panel.Level2.Size', 70, QTAlignment.Right, TableTotalsVisibility.None, true, false),
    new ColumnParams('panel.Level2.Time', 70, QTAlignment.Left, TableTotalsVisibility.None, false, false),
    new ColumnParams('panel.Level2.AvgPrice', 70, QTAlignment.Right, TableTotalsVisibility.None, false, false),
    new ColumnParams('panel.Level2.TotalVol', 70, QTAlignment.Right, TableTotalsVisibility.None, false, false),
    new ColumnParams('panel.Level2.Source', 70, QTAlignment.Left, TableTotalsVisibility.None, false, false, false, false, 0),
    new ColumnParams('panel.Level2.Number', 70, QTAlignment.Right, TableTotalsVisibility.None, false, false),
    new ColumnParams('panel.Level2.DayTradeVolume', 70, QTAlignment.Right, TableTotalsVisibility.None, false, false, true),
    /* 9 */ new ColumnParams('panel.Level2.Contr. CCY value', 70, QTAlignment.Right, TableTotalsVisibility.None, false, false),
    /* 10 */ new ColumnParams('panel.Level2.Contr. CCY Total value', 70, QTAlignment.Right, TableTotalsVisibility.None, false, false),
    new ColumnParams('panel.Level2.Age', 70, QTAlignment.Right, TableTotalsVisibility.None, false, false),
    new ColumnParams('panel.Level2.NumberOfOrders', 70, QTAlignment.Right, TableTotalsVisibility.None, false, false)
];
export var MDLotsMode;
(function (MDLotsMode) {
    MDLotsMode[MDLotsMode["General"] = 0] = "General";
    MDLotsMode[MDLotsMode["RealSize"] = 1] = "RealSize";
    MDLotsMode[MDLotsMode["Lots"] = 2] = "Lots";
})(MDLotsMode || (MDLotsMode = {}));
